import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { useApolloClient } from '@apollo/client'
import { StatusVariant } from '@factorialco/f0-react'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, FileFilled, Upload } from '@factorialco/f0-react/icons/app'
import { LOCAL_STORAGE_KEYS } from 'config/Vars'
import {
  AtsCandidatesDataCollectionDocument,
  AtsCandidatesDataCollectionItemFragment,
  AtsCandidatesDataCollectionJobPostingsFilterDocument,
  AtsCandidatesDataCollectionJobPostingsFilterFragment,
  AtsCandidatesDataCollectionSourcesFilterDocument,
  AtsCandidatesDataCollectionSourcesFilterFragment,
  AtsCandidatesDataCollectionTagsFilterDocument,
  AtsCandidatesDataCollectionTagsFilterFragment,
  AtsCandidatesDataCollectionToolbarActionsDocument,
  AtsCandidatesUpdateTalentPoolDocument,
  AtsCreatedApplicationsfromCandidatesFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import asyncAction from 'modules/core/lib/asyncAction'
import { convertDate, dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { phaseColor } from 'modules/ats/components/ApplicationPhaseTag'
import { DeleteCandidateModal } from 'modules/ats/components/DeleteCandidateModal/DeleteCandidateModal'
import { UploadCvsModal } from 'modules/ats/components/UploadCvsModal'
import { AddCandidatesToJobPostingModal } from 'modules/ats/components/candidates/AddCandidateToJobPostingModal'
import { TalentPoolConfirmationModal } from 'modules/ats/components/candidates/TalentPoolConfirmationModal'
import { isConsentExpired } from 'modules/ats/lib/utils/candidate'
import { getSourceLabel } from 'modules/ats/lib/utils/candidateSources'

const getAverageRatingStatus = (rating: number) => {
  if (rating <= 2) return 'critical'
  if (rating === 3) return 'warning'
  return 'positive'
}

interface Props {
  showUploadCvsButton: boolean
}
export const CandidatesDataCollection = ({ showUploadCvsButton }: Props) => {
  const { navigation } = useNavigation()
  const navigate = useNavigate()
  const client = useApolloClient()
  const { company } = useCurrentSession()

  const [selectedCandidateIds, setSelectedCandidateIds] = useState<number[] | null>(null)
  const [selectedCandidate, setSelectedCandidate] = useState<number | null>(null)

  const [showModal, setModal] = useState<
    'upload-cv' | 'add-to-job-posting' | 'delete-candidate' | 'talent-pool' | null
  >(null)

  const { data: toolbarData } = useQuery(AtsCandidatesDataCollectionToolbarActionsDocument, {
    variables: { companyId: company.id },
  })

  const [updateTalentPool] = useMutation(AtsCandidatesUpdateTalentPoolDocument, {
    extractorFn: (data) => data.atsMutations.updateAtsCandidate,
  })

  const atsImportersFeatureEnabled =
    toolbarData?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'ats_importers'
    )?.enabled ?? false

  const atsBulkImportFeatureEnabled =
    toolbarData?.features.featuresConnection.nodes.find(
      (featureFlag) => featureFlag.name === 'ats_bulk_import'
    )?.enabled ?? false

  const canImportResources = toolbarData?.canImportResources._policies.importResources.can ?? false

  const canAddCandidate = toolbarData?.ats._policies.manageJobPostings.can ?? false
  const canUploadCvs = canAddCandidate && showUploadCvsButton
  const canImportCandidates = canAddCandidate && canImportResources && atsImportersFeatureEnabled
  const canBulkCandidatesImport = canAddCandidate && atsBulkImportFeatureEnabled

  const isConsentLimitEnabled =
    toolbarData?.ats.dataRetentionConfigurationsConnection.nodes[0]?.consentLimitEnabled ?? false

  const onClickAddToJobPosting = (id: number) => {
    setSelectedCandidateIds([id])
    setModal('add-to-job-posting')
    track(trackingEvents.ats.addCandidateToJobOpeningStart)
  }

  useEffect(() => {
    if (showModal === null) {
      setSelectedCandidateIds(null)
      setSelectedCandidate(null)
    }
  }, [showModal])

  const onCloseAddToJobPostingModal = () => {
    track(trackingEvents.ats.addCandidateToJobOpeningCancel)
    setModal(null)
  }

  const onSuccessAddToJobPostingModal = (
    applications: AtsCreatedApplicationsfromCandidatesFragment[]
  ) => {
    if (applications.length > 0) {
      setModal(null)
    } else {
      const application = applications[0]

      if (application?.id) {
        setModal(null)
        track(trackingEvents.ats.addCandidateToJobOpeningSubmit, { is_successful: true })
        navigate(
          navigation.ats.jobs.jobsId.applications.applicationDetail.fullPath({
            jobId: application.jobPosting.id,
            application_id: application.id,
          })
        )
      }
    }
  }
  const updateTalentPoolValue = async (id: number, talentPool: boolean) => {
    const { success } = await asyncAction(
      () => updateTalentPool({ variables: { id, talentPool: !talentPool } }),
      {
        successMessage: talentPool
          ? i18n.t('common.removed_successfully')
          : i18n.t('common.saved_successfully'),
      }
    )
    if (!talentPool) {
      track(trackingEvents.ats.addCandidateToTalentPool, {
        is_successful: success,
      })
    }
  }

  const onClickTalentPool = async ({
    id,
    consentToTalentPool,
    talentPool,
  }: {
    id: number
    consentToTalentPool: boolean
    talentPool: boolean
  }) => {
    const dontShowConfirmation =
      consentToTalentPool ||
      SafeLocalStorage.getItem(LOCAL_STORAGE_KEYS.atsTalentPoolConsentMessage, JSON.parse)

    if (talentPool || dontShowConfirmation) {
      await updateTalentPoolValue(id, talentPool)
    } else {
      setSelectedCandidate(id)
      setModal('talent-pool')
    }
  }

  const source = useGraphqlDataCollectionSource({
    document: AtsCandidatesDataCollectionDocument,
    mapData: (data) => data.ats.candidatesConnection.nodes,
    mapPagination: (data) => ({
      total: data.ats.candidatesConnection.totalCount ?? 0,
    }),
    perPage: 20,
    mapVariables: ({ search, filters, sortings }) => {
      const jobPostingIds =
        filters?.jobPostingIds && filters?.jobPostingIds.length > 0
          ? { in: filters.jobPostingIds.map(Number).filter(Boolean) }
          : undefined

      const sourceIds =
        filters?.sources && filters?.sources.length > 0
          ? { in: filters.sources.map(Number).filter(Boolean) }
          : undefined

      const minimumAverageRating =
        filters?.rating && filters?.rating.length > 0
          ? { gteq: Math.min(...filters.rating.map(Number).filter(Boolean)) }
          : undefined

      const atsTagIds =
        filters?.tags && filters?.tags.length > 0
          ? { in: filters.tags.map(Number).filter(Boolean) }
          : undefined
      const consent =
        isConsentLimitEnabled && filters?.consent && filters?.consent.length > 0
          ? { in: filters.consent.filter(Boolean) }
          : undefined

      return {
        active: filters?.status?.includes('active') ?? false,
        archived: filters?.status?.includes('archived') ?? false,
        atsTagIds,
        consent,
        jobPostingIds,
        minimumAverageRating,
        search,
        sort: sortings?.[0],
        sourceIds,
        talentPool: filters?.status?.includes('talent_pool') ?? false,
      }
    },
    defaultSortings: {
      field: 'createdAt',
      order: 'desc',
    },
    search: {
      enabled: true,
    },
    filters: {
      status: {
        label: i18n.t('ats.candidates.list.filters.status_label'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('ats.candidates.list.filters.active_option'), value: 'active' },
            {
              label: i18n.t('ats.candidates.list.filters.talent_pool_option'),
              value: 'talent_pool',
            },
            { label: i18n.t('ats.candidates.list.filters.archived_option'), value: 'archived' },
          ],
        },
      },
      jobPostingIds: {
        label: i18n.t('ats.candidates.list.filters.job_postings_label'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsCandidatesDataCollectionJobPostingsFilterDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.jobPostingsConnection.nodes.map(
                (jobPosting: AtsCandidatesDataCollectionJobPostingsFilterFragment) => ({
                  label: jobPosting.title || '',
                  value: jobPosting.id,
                })
              ) || []
            )
          },
        },
      },
      sources: {
        label: i18n.t('ats.candidates.list.filters.sources_label'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsCandidatesDataCollectionSourcesFilterDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.candidateSources.map(
                (candidateSource: AtsCandidatesDataCollectionSourcesFilterFragment) => ({
                  label: getSourceLabel(candidateSource.name) || '',
                  value: candidateSource.id,
                })
              ) || []
            )
          },
        },
      },
      rating: {
        label: i18n.t('ats.candidates.list.filters.rating_label'),
        type: 'in',
        options: {
          options: [
            { label: i18n.t('ats.candidates.list.filters.rating_options.1'), value: 1 },
            { label: i18n.t('ats.candidates.list.filters.rating_options.2'), value: 2 },
            { label: i18n.t('ats.candidates.list.filters.rating_options.3'), value: 3 },
            { label: i18n.t('ats.candidates.list.filters.rating_options.4'), value: 4 },
            { label: i18n.t('ats.candidates.list.filters.rating_options.5'), value: 5 },
          ],
        },
      },
      ...(isConsentLimitEnabled && {
        consent: {
          label: i18n.t('ats.candidates.list.filters.consent_label'),
          type: 'in',
          options: {
            options: [
              {
                label: i18n.t('ats.candidates.list.filters.consent_options.not_given'),
                value: 'no_consent_given',
              },
              {
                label: i18n.t('ats.candidates.list.filters.consent_options.given'),
                value: 'not_expired',
              },
              {
                label: i18n.t('ats.candidates.list.filters.consent_options.expired'),
                value: 'expired',
              },
            ],
          },
        },
      }),
      tags: {
        label: i18n.t('ats.candidates.list.filters.tags_label'),
        type: 'in',
        options: {
          cache: true,
          options: async () => {
            const result = await client.query({
              query: AtsCandidatesDataCollectionTagsFilterDocument,
              fetchPolicy: 'cache-first',
            })

            return (
              result.data?.ats.tags.map((tag: AtsCandidatesDataCollectionTagsFilterFragment) => ({
                label: tag.name,
                value: tag.id,
              })) || []
            )
          },
        },
      },
    },
    presets: [
      {
        label: i18n.t('ats.candidates.list.filters.active_option'),
        filter: { status: ['active'] },
      },
      {
        label: i18n.t('ats.candidates.list.filters.talent_pool_option'),
        filter: { status: ['talent_pool'] },
      },
      {
        label: i18n.t('ats.candidates.list.filters.archived_option'),
        filter: { status: ['archived'] },
      },
    ],
    itemUrl: (candidate: AtsCandidatesDataCollectionItemFragment) =>
      navigation.ats.candidates.id.applications.fullPath({ id: candidate.id }),
    primaryActions: () => {
      if (canAddCandidate) {
        return {
          label: i18n.t('ats.candidates.list.actions.new_candidate'),
          icon: Add,
          onClick: () => navigate(navigation.ats.candidates.new.fullPath()),
        }
      }
    },
    secondaryActions: () => {
      return [
        canUploadCvs && {
          label: i18n.t('ats.candidates.list.actions.upload_cvs'),
          icon: FileFilled,
          onClick: () => setModal('upload-cv'),
        },
        canAddCandidate && {
          label: i18n.t('ats.candidates.list.actions.add_internal_candidate'),
          icon: Add,
          onClick: () => navigate(navigation.ats.candidates.newEmployeeCandidate.fullPath()),
        },
        canBulkCandidatesImport && {
          label: i18n.t('ats.candidates.list.actions.import_candidates'),
          icon: Upload,
          onClick: () => navigate(navigation.ats.candidates.import.fullPath()),
        },
        canImportCandidates && {
          label: i18n.t('ats.candidates.list.actions.import_candidates_new'),
          icon: Upload,
          onClick: () =>
            navigate(navigation.ats.candidates.importCandidates.fullPath().replace(/\*+$/, '')),
        },
      ].filter(Boolean)
    },
    itemActions: (candidate: AtsCandidatesDataCollectionItemFragment) => [
      {
        label: i18n.t('ats.candidates.list.item_actions.add_to_job_opening'),
        onClick: () => onClickAddToJobPosting(candidate.id),
      },
      {
        label: candidate.talentPool
          ? i18n.t('ats.candidates.list.item_actions.remove_from_talent_pool')
          : i18n.t('ats.candidates.list.item_actions.add_to_talent_pool'),
        onClick: () =>
          onClickTalentPool({
            id: candidate.id,
            consentToTalentPool: candidate.consentToTalentPool ?? false,
            talentPool: candidate.talentPool ?? false,
          }),
      },
      {
        type: 'separator',
      },
      {
        label: i18n.t('common.delete'),
        critical: true,
        onClick: () => {
          setSelectedCandidate(candidate.id)
          setModal('delete-candidate')
        },
      },
    ],
    selectable: atsBulkImportFeatureEnabled ? (item) => item.id : undefined,
    bulkActions: () => {
      return {
        primary: [
          {
            label: i18n.t('ats.candidates.list.item_actions.add_to_job_opening'),
            icon: Add,
            id: 'add-to-job-posting-bulk',
          },
        ],
      }
    },
  })

  const getConsentStatus = (
    consentExpirationDate: string | null
  ): { status: StatusVariant; label: string } => {
    if (!consentExpirationDate) {
      return {
        status: 'warning',
        label: i18n.t('ats.candidates.list.consent_expiration_date.pending'),
      }
    }

    if (isConsentExpired(consentExpirationDate)) {
      return {
        status: 'critical',
        label: i18n.t('ats.candidates.list.consent_expiration_date.expired_and_date', {
          date: dateBackendDateTimeToUI(consentExpirationDate),
        }),
      }
    }

    return {
      status: 'positive',
      label: i18n.t('ats.candidates.list.consent_expiration_date.given'),
    }
  }

  return (
    <>
      <OneDataCollection
        source={source}
        onBulkAction={(action, selectedItems) => {
          if (action === 'add-to-job-posting-bulk') {
            const selectedIds = selectedItems.itemsStatus
              .filter((status) => status.checked)
              .map((status) => status.item.id)

            setSelectedCandidateIds(selectedIds)
            setModal('add-to-job-posting')
          }
        }}
        visualizations={[
          {
            type: 'table',
            options: {
              frozenColumns: 1,
              columns: [
                {
                  label: i18n.t('ats.candidates.list.title.full_name'),
                  render: ({
                    applications,
                    atsFiles,
                    firstName,
                    lastName,
                  }: AtsCandidatesDataCollectionItemFragment) => {
                    const applicationWithAvatar = applications.find((application) =>
                      application.files.some((file) => file.name === 'photo')
                    )
                    const avatar =
                      applicationWithAvatar?.files.find((file) => file.name === 'photo') ||
                      atsFiles.find((file) => file.category === 'photo')?.file

                    return {
                      type: 'person',
                      value: {
                        firstName,
                        lastName,
                        src: avatar?.url,
                      },
                    }
                  },
                  sorting: 'fullName',
                },
                {
                  label: i18n.t('ats.candidates.list.title.email'),
                  render: ({ email }: AtsCandidatesDataCollectionItemFragment) => email ?? '',
                },
                {
                  label: i18n.t('ats.candidates.list.title.phone'),
                  render: ({ phoneNumber }: AtsCandidatesDataCollectionItemFragment) =>
                    phoneNumber ?? '',
                },
                {
                  label: i18n.t('ats.candidates.list.title.jobs'),
                  render: ({ applications }: AtsCandidatesDataCollectionItemFragment) => {
                    if (applications.length > 0) {
                      return {
                        type: 'tagList',
                        value: {
                          type: 'dot',
                          max: 2,
                          tags: applications.map((application) => ({
                            text: application.relatedJobPosting.title,
                            color: phaseColor(
                              application.applicationPhase.hiringStage?.name || 'default'
                            ),
                          })),
                        },
                      }
                    }
                    return '-'
                  },
                },
                {
                  label: i18n.t('ats.candidates.list.title.source'),
                  render: ({
                    candidateSource,
                    medium,
                  }: AtsCandidatesDataCollectionItemFragment) => {
                    return {
                      type: 'tag',
                      value: {
                        label:
                          candidateSource && medium
                            ? `${medium} (${getSourceLabel(candidateSource.name)})`
                            : getSourceLabel(candidateSource?.name),
                      },
                    }
                  },
                },
                {
                  label: i18n.t('ats.candidates.list.title.rating'),
                  render: ({ ratingAverage }: AtsCandidatesDataCollectionItemFragment) => {
                    if (ratingAverage) {
                      const rating = Math.floor(ratingAverage)

                      return {
                        type: 'status',
                        value: {
                          status: getAverageRatingStatus(rating),
                          label: rating,
                        },
                      }
                    }
                    return ''
                  },
                },
                {
                  label: i18n.t('ats.candidates.list.title.applied_on'),
                  render: ({ createdAt }: AtsCandidatesDataCollectionItemFragment) =>
                    convertDate(createdAt, 'backend_datetime', 'ui_abbreviated_month_year'),
                },
                ...(isConsentLimitEnabled
                  ? [
                      {
                        label: i18n.t('ats.candidates.list.title.consent'),
                        render: ({
                          consentExpirationDate,
                        }: AtsCandidatesDataCollectionItemFragment) => {
                          return {
                            type: 'status' as const,
                            value: getConsentStatus(consentExpirationDate),
                          }
                        },
                      },
                    ]
                  : []),
                {
                  label: i18n.t('ats.candidates.list.title.tags'),
                  render: ({ tags }: AtsCandidatesDataCollectionItemFragment) => {
                    return {
                      type: 'tagList',
                      value: {
                        type: 'raw',
                        max: 2,
                        tags: tags.map((tag) => ({
                          text: tag.name,
                        })),
                      },
                    }
                  },
                },
              ],
            },
          },
        ]}
      />

      {showModal === 'upload-cv' && (
        <UploadCvsModal
          onClose={() => {
            setModal(null)
          }}
        />
      )}

      {showModal === 'add-to-job-posting' && selectedCandidateIds && (
        <AddCandidatesToJobPostingModal
          candidateIds={selectedCandidateIds}
          onSuccess={(applications) => onSuccessAddToJobPostingModal(applications)}
          onClose={() => {
            setModal(null)
            onCloseAddToJobPostingModal()
          }}
        />
      )}

      {showModal === 'delete-candidate' && selectedCandidate && (
        <DeleteCandidateModal
          candidateId={selectedCandidate}
          onClose={() => {
            setModal(null)
          }}
        />
      )}

      {showModal === 'talent-pool' && selectedCandidate && (
        <TalentPoolConfirmationModal
          candidateId={selectedCandidate}
          onClose={() => {
            setModal(null)
          }}
        />
      )}
    </>
  )
}
